import { CloseCircleFilled } from '@ant-design/icons'
import { App, Button, Progress, ProgressProps } from 'antd'
import { DataList } from 'components/list/data.list'
import { useRouter } from 'next/navigation'
import React, { FC, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useDeleteMeetingRecordMutation,
} from 'graphql/meeting/record/delete.meeting.record.mutation'
import { useRegisterActivity } from 'hooks/use.register.activity'
import { useAppDispatch, useAppSelector } from 'store'
import { deleteRecord } from 'store/meeting'
import { cancelUpload, removeUpload } from 'store/upload'
import { ID } from 'utils/id'
import { captureException } from '@tucan/utils/error-tracking'

type Props = {
  id: ID
}

export const UploadIndicator: FC<Props> = (props) => {
  const router = useRouter()
  const { t, i18n } = useTranslation()
  const { message } = App.useApp()
  const [deleteMeetingRecord] = useDeleteMeetingRecordMutation()
  const registerActivity = useRegisterActivity()
  const upload = useAppSelector(state => {
    return state.upload.entities[props.id]
  })
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (!upload?.finished || upload.failed) {
      return
    }

    if (upload.type == 'record') {
      if (upload.instanceId) {
        void registerActivity('MEETING_TRANSCRIPT_UPLOADED')
      } else {
        void registerActivity('TRANSCRIPT_UPLOADED')
      }
    }

  }, [upload])

  if (!upload) {
    return null
  }

  const onDeleteRecord = async (meetingRecordId: ID) => {
    try {
      await deleteMeetingRecord({
        variables: {
          id: meetingRecordId,
        },
      })

      dispatch(deleteRecord(meetingRecordId))
      void message.success(t('meeting-transcript-deleted-successfully', 'Meeting Transcript deleted'))
      void registerActivity('MEETING_TRANSCRIPT_UPLOAD_CANCELED')
    } catch (e) {
      void message.error(t('meeting-transcript-deleted-failed', 'Failed to delete'))
      captureException(e)
    }
  }

  const onClick = () => {
    if (upload.finished || upload.failed) {
      dispatch(removeUpload(props.id))
    }

    if (upload.type == 'record') {
      if (upload.instanceId) {
        router.push(`/${i18n.language}/app/instances/${upload.instanceId}/transcripts`)
      } else {
        router.push(`/${i18n.language}/app/transcripts/${props.id}/edit`)
      }
    }
  }

  let status: ProgressProps['status']

  if (upload.failed) {
    status = 'exception'
  }

  if (upload.finished) {
    status = 'success'
  }

  if (!upload.failed && !upload.finished) {
    status = 'active'
  }

  return (
    <DataList.Item>
      <div
        style={{
          cursor: 'pointer',
          width: 320,
        }}
      >
        <div
          style={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between' }}
        >
          <Progress
            percent={Math.round(upload.progress * 100)}
            status={status}
            style={{ width: '96%' }}
          />
          {
            status === 'active' && (
              <Button
                onClick={() => {
                  void registerActivity('TRANSCRIPT_UPLOAD_CANCELED')
                  void dispatch(cancelUpload(props.id))

                  if (upload.type == 'record') {
                    if (upload.meetingRecordId) {
                      void onDeleteRecord(upload.meetingRecordId)
                    }
                  }
                }}
                style={{ color: 'red', marginLeft: '4px', marginTop: '4px' }}
              >
                {t('upload-cancel', 'Cancel')}
              </Button>
            )
          }
          {
            (status === 'success' || status === 'exception') && (
              <Button
                onClick={() => {
                  void dispatch(removeUpload(props.id))
                }}
                style={{ color: 'red', marginLeft: '4px', marginTop: '4px' }}
              >
                <CloseCircleFilled />
              </Button>
            )
          }
        </div>
        <span
          onClick={onClick}
          style={{
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {upload.name}
        </span>
      </div>
    </DataList.Item>
  )
}
